/**
 * Tenant contracts — provisioning (admin panel) and reads.
 *
 * Creating a tenant creates three things in one transaction: the tenant row, its
 * enabled feature flags, and the owner user (with **no** password). A single-use
 * 24h set-password token is then issued and emailed to the owner.
 *
 * SECURITY: `createTenantResponseSchema` carries the token's *expiry*, never the
 * token itself. The token value exists only inside the onboarding email.
 */
import { z } from 'zod';

import {
  emailSchema,
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  phoneSchema,
  sortDirectionSchema,
  uuidSchema,
} from '../common.js';
import { featureSchema } from '../enums.js';

/* -------------------------------------------------------------------------- */
/* Organisation details                                                        */
/* -------------------------------------------------------------------------- */

export const tenantOrganisationSchema = z.object({
  /** Registered legal entity name, as distinct from the workspace name. */
  legalName: z.string().trim().min(2).max(200),
  website: z.string().trim().url().max(300).optional(),
  addressLine1: z.string().trim().max(200).optional(),
  addressLine2: z.string().trim().max(200).optional(),
  city: z.string().trim().max(120).optional(),
  state: z.string().trim().max(120).optional(),
  postalCode: z.string().trim().max(32).optional(),
  /** ISO 3166-1 alpha-2, e.g. `IN`. */
  country: z.string().trim().toUpperCase().length(2).optional(),
  /** GSTIN / VAT / other tax registration number. */
  taxId: z.string().trim().max(64).optional(),
});
export type TenantOrganisation = z.infer<typeof tenantOrganisationSchema>;

/** Read shape: every organisation column is nullable in the database. */
export const tenantOrganisationViewSchema = tenantOrganisationSchema.partial();
export type TenantOrganisationView = z.infer<typeof tenantOrganisationViewSchema>;

/* -------------------------------------------------------------------------- */
/* Create                                                                      */
/* -------------------------------------------------------------------------- */

export const tenantOwnerInputSchema = z.object({
  name: z.string().trim().min(2).max(120),
  /** Becomes `users.email`, which is globally unique across all tenants. */
  email: emailSchema,
  phone: phoneSchema.optional(),
});
export type TenantOwnerInput = z.infer<typeof tenantOwnerInputSchema>;

/** At least one feature, no duplicates. */
export const tenantFeaturesInputSchema = z
  .array(featureSchema)
  .min(1, 'Enable at least one feature')
  .refine((values) => new Set(values).size === values.length, 'Features must be unique');

export const createTenantRequestSchema = z.object({
  /** Workspace name shown in the product. */
  name: z.string().trim().min(2).max(120),
  /** Primary contact email for the tenant (billing/ops), not a login. */
  email: emailSchema,
  phone: phoneSchema,
  organisation: tenantOrganisationSchema,
  /** Subscription/licence key. Unique across tenants. */
  subscriptionKey: z.string().trim().min(4).max(120),
  /** Subscription expiry, ISO-8601 with offset. */
  validTill: isoDateTimeSchema,
  features: tenantFeaturesInputSchema,
  /** The first user; receives the set-password link. */
  owner: tenantOwnerInputSchema,
});
export type CreateTenantRequest = z.infer<typeof createTenantRequestSchema>;

/* -------------------------------------------------------------------------- */
/* Read                                                                        */
/* -------------------------------------------------------------------------- */

export const tenantSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  email: z.string().email(),
  phone: z.string(),
  organisation: tenantOrganisationViewSchema,
  subscriptionKey: z.string(),
  validTill: isoDateTimeSchema,
  isActive: z.boolean(),
  /** Enabled features only. */
  features: z.array(featureSchema),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type Tenant = z.infer<typeof tenantSchema>;

/** Row shape for the admin tenant list. */
export const tenantSummarySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  email: z.string().email(),
  features: z.array(featureSchema),
  validTill: isoDateTimeSchema,
  isActive: z.boolean(),
  ownerEmail: z.string().email().nullable(),
  userCount: z.number().int().min(0),
  createdAt: isoDateTimeSchema,
});
export type TenantSummary = z.infer<typeof tenantSummarySchema>;

export const TENANT_SORT_VALUES = ['createdAt', 'name', 'validTill'] as const;
export const tenantSortSchema = z.enum(TENANT_SORT_VALUES);
export type TenantSort = z.infer<typeof tenantSortSchema>;

export const tenantListQuerySchema = paginationQuerySchema.extend({
  /** Matches tenant name or contact email, case-insensitive. */
  search: z.string().trim().max(120).optional(),
  feature: featureSchema.optional(),
  isActive: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((value) => (typeof value === 'boolean' ? value : value === 'true'))
    .optional(),
  sort: tenantSortSchema.default('createdAt'),
  direction: sortDirectionSchema.default('desc'),
});
export type TenantListQuery = z.infer<typeof tenantListQuerySchema>;

export const tenantListResponseSchema = paginated(tenantSummarySchema);
export type TenantListResponse = z.infer<typeof tenantListResponseSchema>;

export const tenantParamsSchema = z.object({ tenantId: uuidSchema });
export type TenantParams = z.infer<typeof tenantParamsSchema>;

export const tenantResponseSchema = z.object({ tenant: tenantSchema });
export type TenantResponse = z.infer<typeof tenantResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Create response                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Confirms provisioning and tells the admin when the onboarding link dies.
 * It must NEVER carry the set-password token or a password.
 */
export const createTenantResponseSchema = z.object({
  tenant: tenantSchema,
  owner: z.object({
    id: uuidSchema,
    email: z.string().email(),
    name: z.string(),
  }),
  /** When the onboarding email was handed to the mail transport. */
  setPasswordEmailSentAt: isoDateTimeSchema,
  /** Exactly 24h after issue. */
  setPasswordTokenExpiresAt: isoDateTimeSchema,
});
export type CreateTenantResponse = z.infer<typeof createTenantResponseSchema>;
